import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Dropdown,
  Menu,
  Button,
  Icon,
  Label,
  Sidebar,
  Segment,
} from "semantic-ui-react";
import { Link, useLocation } from "react-router-dom";
import { FrontSiteRoutes } from "@routes/urls";
import FeedbackContext from "@pages/frontsite/Feedback/FeedbackContext";
import { authenticationService } from "@authentication/services/AuthenticationService";

const DashboardHeader = ({
  dashboardHeaderHeight = "60px",
  feedbackCount = 0,
  isAllowedToEdit = false,
  activeTab = "advisory",
  onTabChange,
  user,
  showTabs = true,
}) => {
  const location = useLocation();
  const context = useContext(FeedbackContext);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const liveFeedbackCount =
    context && typeof context.feedbackCount === "number"
      ? context.feedbackCount
      : feedbackCount;

  const toggleSidebar = () => setSidebarVisible(!sidebarVisible);
  const closeSidebar = () => setSidebarVisible(false);

  const handleTabChange = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    }
    closeSidebar();
  };

  const canAccessOPAC = (user) => {
    if (!user) return true;
    return !authenticationService.hasRoles(user, ["admin"]);
  };

  // Determine if a menu item should be highlighted based on the current route
  const isActiveRoute = (route) => {
    return location.pathname.includes(route);
  };

  // Mobile menu items
  const MobileMenuItems = () => (
    <div style={{ padding: "1em" }}>
      {/* About Section */}
      <div style={{ marginBottom: "1.5em" }}>
        <div
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            marginBottom: "0.5em",
            color: "#333",
          }}
        >
          About
          {isAllowedToEdit && liveFeedbackCount > 0 && (
            <Label
              color="green"
              circular
              size="mini"
              style={{ marginLeft: "0.5em" }}
            >
              {liveFeedbackCount}
            </Label>
          )}
        </div>
        <div style={{ paddingLeft: "1em" }}>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/about"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/about") ? "#21ba45" : "#666",
                textDecoration: "none",
                fontWeight: isActiveRoute("/about") ? "bold" : "normal",
              }}
            >
              About Us
            </Link>
          </div>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/contact"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/contact") ? "#21ba45" : "#666",
                textDecoration: "none",
                fontWeight: isActiveRoute("/contact") ? "bold" : "normal",
              }}
            >
              Contact Us
            </Link>
          </div>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/feedback"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/feedback") ? "#21ba45" : "#666",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontWeight: isActiveRoute("/feedback") ? "bold" : "normal",
              }}
            >
              <span>Library Feedback</span>
              {isAllowedToEdit && liveFeedbackCount > 0 && (
                <Label color="green" circular size="mini">
                  {liveFeedbackCount}
                </Label>
              )}
            </Link>
          </div>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/library-policies"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/library-policies") ? "#21ba45" : "#666",
                textDecoration: "none",
                fontWeight: isActiveRoute("/library-policies")
                  ? "bold"
                  : "normal",
              }}
            >
              Library Policy
            </Link>
          </div>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/download"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/download") ? "#21ba45" : "#666",
                textDecoration: "none",
                fontWeight: isActiveRoute("/download") ? "bold" : "normal",
              }}
            >
              Citizen Charter
            </Link>
          </div>
        </div>
      </div>
      {/* Services Section */}
      <div style={{ marginBottom: "1.5em" }}>
        <div
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            marginBottom: "0.5em",
            color: "#333",
          }}
        >
          Services
        </div>
        <div style={{ paddingLeft: "1em" }}>
          <div style={{ marginBottom: "0.5em" }}>
            {canAccessOPAC(user) && (
              <Link
                to={FrontSiteRoutes.home}
                onClick={closeSidebar}
                style={{
                  color: isActiveRoute(FrontSiteRoutes.home)
                    ? "#21ba45"
                    : "#666",
                  textDecoration: "none",
                  fontWeight: isActiveRoute(FrontSiteRoutes.home)
                    ? "bold"
                    : "normal",
                }}
              >
                Web OPAC
              </Link>
            )}
          </div>
          <div style={{ marginBottom: "0.5em" }}>
            <Link
              to="/eresources"
              onClick={closeSidebar}
              style={{
                color: isActiveRoute("/eresources") ? "#21ba45" : "#666",
                textDecoration: "none",
                fontWeight: isActiveRoute("/eresources") ? "bold" : "normal",
              }}
            >
              E-Resources
            </Link>
          </div>
        </div>
      </div>
      {/* Tab Actions */}
      <div style={{ marginBottom: "1.5em" }}>
        <div
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            marginBottom: "0.5em",
            color: "#333",
          }}
        >
          View
        </div>
        <div style={{ paddingLeft: "1em" }}>
          <div
            style={{
              marginBottom: "0.5em",
              cursor: "pointer",
              color: activeTab === "advisory" ? "#21ba45" : "#666",
              fontWeight: activeTab === "advisory" ? "bold" : "normal",
            }}
            onClick={() => {
              handleTabChange("advisory");
              window.location.href = FrontSiteRoutes.dashboard;
            }}
          >
            <Icon name="bullhorn" /> Announcements
          </div>
          <div
            style={{
              marginBottom: "0.5em",
              cursor: "pointer",
              color: activeTab === "calendar" ? "#21ba45" : "#666",
              fontWeight: activeTab === "calendar" ? "bold" : "normal",
            }}
            onClick={() => {
              handleTabChange("calendar");
              window.location.href = `${FrontSiteRoutes.dashboard}?tab=calendar`;
            }}
          >
            <Icon name="calendar alternate outline" /> Calendar
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Header */}
      <Menu
        secondary
        fixed="top"
        className="desktop-only"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(10px)",
          top: "130px",
          left: 0,
          right: 0,
          height: dashboardHeaderHeight,
          zIndex: 2,
          borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          padding: "0 1em",
        }}
      >
        <Container
          fluid
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT: Branding */}
          <Menu.Item as={Link} to="/dashboard" header>
            <Icon name="dashboard" color="green" />
            <span
              style={{
                fontWeight: 600,
                fontSize: "1rem",
                marginLeft: "0.5em",
              }}
            >
              Dashboard
            </span>
          </Menu.Item>
          {/* CENTER: Navigation */}
          <Menu.Menu
            style={{ display: "flex", alignItems: "center", gap: "1em" }}
          >
            <Dropdown
              data-tour="dashboard-about"
              item
              simple
              trigger={
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25em",
                  }}
                >
                  About
                  {isAllowedToEdit && liveFeedbackCount > 0 && (
                    <Label
                      color="green"
                      circular
                      size="mini"
                      style={{
                        marginLeft: "0.2em",
                        marginTop: "0.2em",
                      }}
                    >
                      {liveFeedbackCount}
                    </Label>
                  )}
                </span>
              }
            >
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/about">
                  About Us
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/contact">
                  Contact Us
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/feedback">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Library Feedback</span>
                    {isAllowedToEdit && liveFeedbackCount > 0 && (
                      <Label
                        color="green"
                        circular
                        size="mini"
                        style={{ marginLeft: "0.5em" }}
                      >
                        {liveFeedbackCount}
                      </Label>
                    )}
                  </div>
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/library-policies">
                  Library Policy
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/download">
                  Citizen Charter
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              data-tour="dashboard-services"
              item
              text="Services"
              simple
            >
              <Dropdown.Menu>
                {canAccessOPAC(user) && (
                  <Dropdown.Item as={Link} to={FrontSiteRoutes.home}>
                    Web OPAC
                  </Dropdown.Item>
                )}
                <Dropdown.Item as={Link} to="/eresources">
                  E-Resources
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Menu>
          {/* RIGHT: Tab Toggle Buttons */}
          <Menu.Item position="right">
            <Button.Group size="small">
              <Button
                data-tour="dashboard-post-announcement"
                icon
                color={activeTab === "advisory" ? "green" : undefined}
                basic={activeTab !== "advisory"}
                as={Link}
                to={FrontSiteRoutes.dashboard}
                onClick={() => onTabChange && onTabChange("advisory")}
                title="View Announcements"
                style={{
                  pointerEvents: "auto",
                  cursor: "pointer",
                  zIndex: 1001,
                }}
              >
                <Icon name="bullhorn" />
              </Button>
              <Button
                data-tour="dashboard-calendar"
                icon
                color={activeTab === "calendar" ? "green" : undefined}
                basic={activeTab !== "calendar"}
                as={Link}
                to={`${FrontSiteRoutes.dashboard}?tab=calendar`}
                onClick={() => onTabChange && onTabChange("calendar")}
                title="View Calendar"
                style={{
                  pointerEvents: "auto",
                  cursor: "pointer",
                  zIndex: 1001,
                }}
              >
                <Icon name="calendar alternate outline" />
              </Button>
            </Button.Group>
          </Menu.Item>
        </Container>
      </Menu>

      {/* Mobile Header */}
      <Menu
        secondary
        fixed="top"
        className="mobile-only"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          top: "130px",
          left: 0,
          right: 0,
          height: dashboardHeaderHeight,
          zIndex: 2,
          borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          padding: "0 1em",
        }}
      >
        <Container
          fluid
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT: Hamburger Menu */}
          <Menu.Item>
            <Button
              icon
              basic
              onClick={toggleSidebar}
              style={{
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
              }}
            >
              <Icon name="bars" size="large" />
            </Button>
          </Menu.Item>
          {/* CENTER: Branding */}
          <Menu.Item
            header
            style={{
              flex: 1,
              textAlign: "center",
              marginTop: "1em",
              marginLeft: "2em",
            }}
          >
            <Icon name="dashboard" color="green" />
            <span
              style={{
                fontWeight: 600,
                fontSize: "1rem",
                marginLeft: "0.5em",
              }}
            >
              Dashboard
            </span>
          </Menu.Item>
          {/* RIGHT: Active Tab Indicator */}
          <Menu.Item>
            <Button
              icon
              color="green"
              size="small"
              style={{ minWidth: "40px" }}
            >
              <Icon
                name={
                  activeTab === "advisory"
                    ? "bullhorn"
                    : "calendar alternate outline"
                }
              />
            </Button>
          </Menu.Item>
        </Container>
      </Menu>

      {/* Mobile Sidebar */}
      <Sidebar
        as={Segment}
        animation="overlay"
        direction="left"
        visible={sidebarVisible}
        onHide={closeSidebar}
        style={{
          backgroundColor: "white",
          boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
          zIndex: 3,
          top: `calc(130px + ${dashboardHeaderHeight})`,
          height: `calc(100vh - 130px - ${dashboardHeaderHeight})`,
          width: "280px",
          overflow: "auto",
        }}
      >
        <MobileMenuItems />
      </Sidebar>

      {/* Overlay for closing sidebar */}
      {sidebarVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1,
          }}
          onClick={closeSidebar}
        />
      )}

      {/* CSS for responsive behavior */}
      <style jsx="true">
        {`
          @media (min-width: 769px) {
            .mobile-only {
              display: none !important;
            }
          }
          @media (max-width: 768px) {
            .desktop-only {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
};

DashboardHeader.propTypes = {
  dashboardHeaderHeight: PropTypes.string,
  feedbackCount: PropTypes.number,
  isAllowedToEdit: PropTypes.bool,
  activeTab: PropTypes.oneOf([
    "advisory",
    "calendar",
    "feedback",
    "about",
    "contact",
    "library-policies",
    "download",
    "opac",
    "e-resources",
  ]),
  onTabChange: PropTypes.func,
  user: PropTypes.object,
  showTabs: PropTypes.bool,
};

DashboardHeader.defaultProps = {
  dashboardHeaderHeight: "60px",
  feedbackCount: 0,
  isAllowedToEdit: false,
  activeTab: "advisory",
  showTabs: true,
};

export default DashboardHeader;
